import type { TargetOrganization } from '@/session/identity';
import { scopedApi } from '@/session/scopedClients';
import { GLOBAL_SCOPE } from '@/session/scopedTransport';
import type { EndpointResponse, ErrorResponse } from '@/types/endpoints';
import type { IntegrationAllowance } from '@/types/integration';
import { utils } from '../../utils';
import type { WriteRequestOptions } from '../requestOptions';

type RequestParams = {
  target: TargetOrganization;
  data: Partial<IntegrationAllowance>[];
};

/** A warning from the API's INSERT IGNORE: a row it skipped (such as a duplicate) or stored truncated. */
export type AllowanceInsertError = Readonly<{ level?: string; message?: string }>;

/**
 * Unlike other successes, a bulk create's 201 can carry `errors.insert_errors` beside the rows it saved. `result` holds
 * every row for the submitted organization/integration pairs, existing ones included; `num_results` counts the inserts.
 */
export type BulkCreateIntegrationAllowancesSuccess = Omit<Exclude<EndpointResponse<IntegrationAllowance[]>, ErrorResponse>, 'errors'> & {
  errors?: Readonly<{ insert_errors: AllowanceInsertError[] | null }>;
};

export type BulkCreateIntegrationAllowancesResponse = BulkCreateIntegrationAllowancesSuccess | ErrorResponse;

export const bulkCreateIntegrationAllowances = async (
  { target, data }: RequestParams,
  options: WriteRequestOptions
): Promise<BulkCreateIntegrationAllowancesResponse> => {
  try {
    const stampedData = data.map((row) => ({ ...row, organization_id: target.id }));
    const response = await scopedApi(GLOBAL_SCOPE).post<BulkCreateIntegrationAllowancesResponse>('integration_allowance/bulk', stampedData, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
